import useAnalytics from 'core/hooks/useAnalytics';
import { useUser } from 'core/lite/hooks/useUser';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EVENT_NAMES } from '../constants/eventNames';
import { SESSION_STATUS } from '../constants/sessionStatus';
import practiceService from '../services/practiceService';
import { useAIChatPracticeSession } from '../contexts/AIChatPracticeSession/useAIChatPracticeSession';
/**
 * Custom hook for managing ChatbotPracticeView page state and logic
 * Handles practice activity generation and chatbot integration
 * Follows the project's pattern of encapsulating Context logic in custom hooks
 */
const useChatbotPracticeView = () => {
  const { sessionId, activityInstanceId: urlActivityInstanceId } = useParams();
  const { user } = useUser();
  const { trackEvent } = useAnalytics();

  // Local state for session data
  const [sessionData, setSessionData] = useState(null);

  // Practice exercise state — kept local to this view (not in the chatbot context).
  // Living here means it never depends on `isAIEnabled`: when AI is disabled the context
  // turns its actions into no-ops, which previously left the spinner stuck forever.
  const [exercise, setExercise] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Error reporting state
  const [isReportingError, setIsReportingError] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalType, setStatusModalType] = useState('success');

  const {
    activityInstanceId,
    setActivityInstanceId,
    activityId,
    setActivityId,
    completedActivitiesCount,
    setCompletedActivitiesCount,
    addActivityResult,
    updateSessionKCs,
    resetPracticeSession,
    setActivityExtraInfo,
    activityExtraInfo,
  } = useAIChatPracticeSession();

  // Create configured practice service instance

  /**
   * Track activity viewed event (non-blocking)
   * @param {Object} activity - The activity data
   * @param {Object} session - The session data
   */
  const trackActivityViewedEvent = useCallback(
    (activity, session) => {
      trackEvent(EVENT_NAMES.IA_ACTIVITY_VIEWED, {
        activity_id: activity.activityId,
        activity_session_id: session.id,
        subject: session.subject,
        kc_id: activity.associatedKcReferenceId,
        phase: activity.activityPhase,
        template: activity.data?.type,
      }).catch((error) => console.error('Analytics tracking failed:', error));
    },
    [trackEvent]
  );

  /**
   * Load a specific activity instance from URL parameters using session data
   * @param {string} sessionId - The session ID from URL
   * @param {string} activityInstanceId - The activity instance ID from URL
   */
  const loadActivityInstance = useCallback(
    async (sessionId, activityInstanceId) => {
      try {
        setIsLoading(true);
        setExercise(null);
        setActivityInstanceId(null);
        setActivityId(null);
        setError(null);

        // Get session data which includes all activity instances
        const sessionData = await practiceService.getSession(sessionId);

        if (!sessionData || !sessionData.id) {
          throw new Error('Failed to load session: Invalid response data');
        }

        // Set session data in context for progress bar
        setSessionData(sessionData);

        // Calculate and update completed activities count
        const completedCount = sessionData.activityInstances?.filter((activity) => activity.respondedAt).length || 0;
        setCompletedActivitiesCount(completedCount);

        // If no activityInstanceId provided (summary route), just load session data
        if (!activityInstanceId) {
          // Just loading session data for summary view
          setIsLoading(false);
          return;
        }

        // Find the specific activity instance in the session
        const targetActivity = sessionData.activityInstances?.find((activity) => activity.id === activityInstanceId);

        if (!targetActivity) {
          throw new Error(`Activity instance ${activityInstanceId} not found in session`);
        }

        const transformedData = { ...targetActivity.data };

        // Update shared context state
        setExercise(transformedData);
        setActivityInstanceId(targetActivity.id);
        setActivityId(targetActivity.activityId);
        // Set activity extra info for display
        setActivityExtraInfo({
          activityPhase: targetActivity.activityPhase,
          associatedKcId: targetActivity.associatedKcId,
          associatedKcReferenceId: targetActivity.associatedKcReferenceId,
          template: targetActivity.data.type,
        });

        // Track activity viewed event (non-blocking)
        trackActivityViewedEvent(targetActivity, sessionData);
      } catch (error) {
        console.error('❌ Error loading session and activity instance:', error);
        setError(error.message);
        // Activity loading failed - chat functionality will be disabled
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsLoading,
      setExercise,
      setActivityInstanceId,
      setActivityId,
      setError,
      setSessionData,
      setCompletedActivitiesCount,
      setActivityExtraInfo,
      trackActivityViewedEvent,
    ]
  );

  /**
   * Generate a new practice activity and update chat context
   * @param {string} seedName - Optional seed name for activity generation
   */
  const generateActivity = useCallback(
    async (seedName = '') => {
      try {
        setIsLoading(true);
        setExercise(null);
        setActivityInstanceId(null);
        setActivityId(null);
        setError(null);

        const data = await practiceService.generateActivity(seedName);

        if (!data || !data.activityInstanceId) {
          throw new Error('Failed to generate activity: Invalid response data');
        }

        // Update shared context state
        setExercise(data.data);
        setActivityInstanceId(data.activityInstanceId);
        setActivityId(data.activityId);
        // Set activity extra info for display
        setActivityExtraInfo({
          activityPhase: data.activityPhase,
          associatedKcId: data.associatedKcId,
          associatedKcReferenceId: data.associatedKcReferenceId,
          template: data.template,
        });
      } catch (error) {
        setError(error.message);
        // Activity generation failed - chat functionality will be disabled
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, setExercise, setActivityInstanceId, setActivityId, setError]
  );

  /**
   * Reset the practice activity state
   */
  const resetActivity = useCallback(() => {
    setExercise(null);
    setActivityInstanceId(null);
    setActivityId(null);
    setError(null);
    setIsLoading(true);
  }, [setExercise, setActivityInstanceId, setActivityId, setError, setIsLoading]);

  /**
   * Check if chat functionality is available (activity generation succeeded)
   */
  const isChatAvailable = Boolean(activityInstanceId && !error);

  /**
   * Increment completed activities count
   */
  const incrementCompletedActivities = useCallback(() => {
    setCompletedActivitiesCount(completedActivitiesCount + 1);
  }, [completedActivitiesCount, setCompletedActivitiesCount]);

  /**
   * Add an activity result to the session
   * @param {Object} result - Activity result with score and response data
   */
  const addActivityResultToSession = useCallback(
    (result) => {
      addActivityResult(result);
    },
    [addActivityResult]
  );

  /**
   * Update KC mastery levels based on activity response
   * @param {Object} response - Activity response with KC data
   */
  const updateKCMastery = useCallback(() => {
    // This would normally extract KCs from the response
    // For now, we'll use mock data
    const mockKCs = [
      { name: 'Ubicar puntos en el plano cartesiano a partir de sus coordenadas', score: 0.85 },
      { name: 'Leer e identificar correctamente coordenadas representadas en los ejes', score: 0.9 },
      { name: 'Prestar atención al orden correcto de las coordenadas (x, y) para evitar confusiones', score: 0.7 },
    ];
    updateSessionKCs(mockKCs);
  }, [updateSessionKCs]);

  /**
   * Handle error reporting
   * @param {Object} errorData - The error report data
   */
  const handleReportError = useCallback(() => {
    setShowErrorModal(true);
  }, []);

  /**
   * Submit error report to the server
   * @param {Object} errorData - The error report data
   */
  const handleSubmitError = useCallback(
    async (errorData) => {
      if (!user?.guid || !user?.username || !activityInstanceId) {
        console.error('Missing required data for error report');
        setStatusModalType('error');
        setShowStatusModal(true);
        return;
      }

      setIsReportingError(true);
      setShowErrorModal(false);

      try {
        await practiceService.notifyError({
          userId: user.guid,
          username: user.username,
          tenant: window.location.hostname,
          activityInstanceId,
          activityId,
          errorType: errorData.errorType,
          description: errorData.description || '',
        });

        // Show success modal
        setTimeout(() => {
          setStatusModalType('success');
          setShowStatusModal(true);
          setIsReportingError(false);
        }, 1000);
      } catch (error) {
        console.error('Error reporting issue:', error);
        // Show error modal
        setTimeout(() => {
          setStatusModalType('error');
          setShowStatusModal(true);
          setIsReportingError(false);
        }, 1000);
      }
    },
    [user?.guid, user?.username, activityInstanceId, activityId]
  );

  /**
   * Close error modal
   */
  const handleCloseErrorModal = useCallback(() => {
    setShowErrorModal(false);
  }, []);

  /**
   * Close status modal
   */
  const handleCloseStatusModal = useCallback(() => {
    setShowStatusModal(false);
  }, []);

  // Derived values from current session
  const isSessionComplete = sessionData?.status === SESSION_STATUS.COMPLETED;

  // Build the summary data from the loaded session once it is complete.
  // `isCorrect` is true only when a score exists and equals the activity's max score
  // (a missing score must not be treated as a correct 0 === 0 match).
  const activityResults = useMemo(() => {
    if (!isSessionComplete || !sessionData) return [];
    return (
      sessionData.activityInstances
        ?.filter((activity) => activity.respondedAt)
        .map((activity) => ({
          isCorrect: activity.userResponse?.score != null && activity.userResponse.score === activity.userResponse.max_score,
          response: activity.userResponse,
          activityInstanceId: activity.id,
          associatedKC: activity.associatedKcReferenceId,
        })) || []
    );
  }, [isSessionComplete, sessionData]);

  const sessionKCs = useMemo(() => {
    if (!isSessionComplete || !sessionData) return [];
    return sessionData.sessionKCs || [];
  }, [isSessionComplete, sessionData]);

  return {
    // State
    isLoading,
    exercise,
    activityInstanceId,
    error,
    isChatAvailable,
    completedActivitiesCount,
    activityResults,
    sessionKCs,
    isSessionComplete,
    sessionData,
    activityExtraInfo,

    // Error reporting state
    isReportingError,
    showErrorModal,
    showStatusModal,
    statusModalType,

    // URL parameters
    sessionId,
    urlActivityInstanceId,

    // Actions
    generateActivity,
    loadActivityInstance,
    resetActivity,
    incrementCompletedActivities,
    addActivityResultToSession,
    updateKCMastery,
    resetPracticeSession,

    // Error reporting actions
    handleReportError,
    handleSubmitError,
    handleCloseErrorModal,
    handleCloseStatusModal,
  };
};

export default useChatbotPracticeView;
